import { navigate } from 'gatsby';
import { PageContext } from '../types/template';
import { services } from '../context';
import { addPersistingQueryParamsToPath } from '../helpers/urlHelper';
import {
  updateCountryStateInSearch,
  updatePostCodeInSearch,
} from '../reducers/search/searchReducer';
import { useStateDispatch } from '../store';
import {
  setDisabledPrevButton,
  updateSelectedService,
} from '../reducers/app/appReducer';
import { isNativeAppView, NATIVE_APP } from '../helpers/nativeAppHelper';
import { isPlainView, PLAIN_VIEW } from '../helpers/plainViewHelper';

/* globals CSS_CONFIG */

interface navigateDirectParams {
  onDirectNavigationFailed: () => void;
  zip?: string;
  service?: string;
}

interface UseDirectNavigationAction {
  navigateDirect: (params: navigateDirectParams) => Promise<void>;
}

const useDirectNavigation = (
  pageContext: PageContext,
): [UseDirectNavigationAction] => {
  const dispatch = useStateDispatch();
  const handleZipCode = async (zipCode: string): Promise<void> => {
    const zipCodeRegexp = new RegExp(CSS_CONFIG.REGEXP.zipCode);
    if (!!zipCode && zipCodeRegexp.test(zipCode)) {
      dispatch(updatePostCodeInSearch(zipCode));
    } else {
      throw Error;
    }
  };

  const findStateCode = async (zipCode: string): Promise<string> => {
    const { SERVICES } = CSS_CONFIG;
    let postalCodeState = '';
    if (SERVICES.getServicesUsingStateCode) {
      const postalCodeLookup =
        await services.state.getPostalCodeLocations(zipCode);
      if (postalCodeLookup.valid && postalCodeLookup.locations.length > 0) {
        postalCodeState = postalCodeLookup.locations[0].state;
        dispatch(updateCountryStateInSearch(postalCodeState));
      } else {
        throw Error;
      }
    }
    return postalCodeState;
  };

  /* ikeaapp cleanup: Update function once the app team starts using /app instead of /ikeaapp */
  const getDirectPathForApp = (page: string): string => {
    if (isNativeAppView()) {
      return `/${NATIVE_APP}/${pageContext.operation}/${page}/`;
    }

    if (isPlainView()) {
      return `/${PLAIN_VIEW}/${pageContext.operation}/${page}/`;
    }

    return `/${pageContext.operation}/${page}/`;
  };

  const navigateToServices = async (zipCode: string): Promise<void> => {
    dispatch(setDisabledPrevButton('services'));
    await handleZipCode(zipCode);
    await findStateCode(zipCode);
    const path = getDirectPathForApp('services');
    await navigate(addPersistingQueryParamsToPath(path));
  };

  const navigateToTimeSlots = async (
    zipCode: string,
    service: string,
  ): Promise<void> => {
    dispatch(setDisabledPrevButton('timeslots'));
    dispatch(
      updateSelectedService({
        serviceId: service.toUpperCase(),
        serviceCode: '',
      }),
    );
    await handleZipCode(zipCode);
    await findStateCode(zipCode);
    const path = getDirectPathForApp('timeslots');
    await navigate(addPersistingQueryParamsToPath(path));
  };

  const navigateDirect = async ({
    onDirectNavigationFailed,
    zip,
    service,
  }: navigateDirectParams): Promise<void> => {
    try {
      if (zip && service) {
        await navigateToTimeSlots(zip, service);
      } else if (zip) {
        await navigateToServices(zip);
      }
    } catch (error) {
      const path = `/${pageContext.operation}/`;
      await navigate(addPersistingQueryParamsToPath(path));
      onDirectNavigationFailed();
    }
  };

  return [{ navigateDirect }];
};

export default useDirectNavigation;
